// No-API-key integration helpers: Google Maps deep links, .ics export, Xero CSV.

export function mapsDirections(origin, destination) {
  const o = encodeURIComponent(origin || "");
  const d = encodeURIComponent(destination || "");
  return `https://www.google.com/maps/dir/?api=1&origin=${o}&destination=${d}`;
}

export function mapsSearch(query) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query || "")}`;
}

function download(filename, content, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function icsDate(d) {
  // all-day events: YYYYMMDD
  const dt = new Date(d);
  if (isNaN(dt)) return null;
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}${m}${day}`;
}

function esc(s) {
  return String(s || "").replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");
}

// events: [{title, date, description, location}]
export function downloadICS(events, filename = "dermalis-calendar.ics") {
  const lines = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Dermalis CRM//EN", "CALSCALE:GREGORIAN"];
  const stamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  events.forEach((e, i) => {
    const start = icsDate(e.date);
    if (!start) return;
    const endDt = new Date(e.date);
    endDt.setDate(endDt.getDate() + 1);
    const end = icsDate(endDt);
    lines.push("BEGIN:VEVENT");
    lines.push(`UID:dermalis-${i}-${start}@dermalis.crm`);
    lines.push(`DTSTAMP:${stamp}`);
    lines.push(`DTSTART;VALUE=DATE:${start}`);
    lines.push(`DTEND;VALUE=DATE:${end}`);
    lines.push(`SUMMARY:${esc(e.title)}`);
    if (e.description) lines.push(`DESCRIPTION:${esc(e.description)}`);
    if (e.location) lines.push(`LOCATION:${esc(e.location)}`);
    lines.push("END:VEVENT");
  });
  lines.push("END:VCALENDAR");
  download(filename, lines.join("\r\n"), "text/calendar");
}

// Open a print-ready window with the given inner HTML and trigger the browser's
// print dialog (which includes "Save as PDF"). No external library required.
export function printHTML(innerHTML, title = "Document") {
  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
  <style>
    @page { margin: 18mm 16mm; }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { font-family: 'Inter', -apple-system, Segoe UI, sans-serif; color: #0F172A; margin: 0; }
    .head { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:3px solid #0F172A; padding-bottom:14px; margin-bottom:22px; }
    .brand { font-weight:800; font-size:20px; }
    .brand small { display:block; font-weight:600; font-size:10px; letter-spacing:2px; color:#64748B; text-transform:uppercase; }
    .meta { text-align:right; font-size:12px; color:#475569; }
    h2 { font-size:13px; text-transform:uppercase; letter-spacing:1px; color:#2563EB; margin:24px 0 8px; }
    table { width:100%; border-collapse:collapse; font-size:12.5px; }
    th { text-align:left; font-size:10px; text-transform:uppercase; letter-spacing:.5px; color:#94A3B8; padding:6px 8px; border-bottom:1px solid #E2E8F0; }
    td { padding:7px 8px; border-bottom:1px solid #F1F5F9; vertical-align:top; }
    .pill { display:inline-block; font-size:10px; padding:2px 8px; border-radius:99px; border:1px solid #E2E8F0; color:#475569; }
    .overdue { color:#DC2626; font-weight:600; }
    .empty { color:#94A3B8; font-size:12px; padding:8px; }
    .stats { display:flex; gap:24px; margin-bottom:8px; }
    .stat b { display:block; font-size:22px; }
    .stat span { font-size:11px; color:#64748B; }
    .foot { margin-top:30px; padding-top:12px; border-top:1px solid #E2E8F0; font-size:10px; color:#94A3B8; }
  </style></head><body>${innerHTML}
  <script>window.onload=function(){setTimeout(function(){window.print();},300);};<\/script>
  </body></html>`);
  w.document.close();
}

export function exportXeroInvoices(opps) {
  const headers = [
    "ContactName", "InvoiceNumber", "InvoiceDate", "DueDate", "Description",
    "Quantity", "UnitAmount", "AccountCode", "TaxType", "Currency",
  ];
  const fmtDate = (d) => {
    const dt = new Date(d);
    if (isNaN(dt)) return "";
    return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;
  };
  const rows = opps.map((o, i) => {
    const date = o.close_date || o.updated_at;
    const due = new Date(date); due.setDate(due.getDate() + 20);
    return [
      o.clinic_name || "", `INV-${String(1000 + i)}`, fmtDate(date), fmtDate(due),
      `${o.product || "Product"}${o.discount ? ` (${o.discount}% disc)` : ""}`,
      "1", Number(o.value || 0).toFixed(2), "200", "GST on Income", "NZD",
    ];
  });
  const csv = [headers, ...rows]
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
    .join("\r\n");
  download("xero-sales-invoices.csv", csv, "text/csv");
}
